/**
 * Distâncias medidas na cena (a régua e o caminho do movimento) na unidade que cada pessoa
 * prefere (Configurações → Geral → Distâncias). A cena guarda a escala com a unidade que o mestre
 * escreveu (`cellUnit`, texto livre). Metros e pés se convertem pela conta dos livros de D&D,
 * 1,5 m = 5 pés, e não pelos 3,28 pés de um metro: um quadrado fica com 5 pés redondos.
 */
import type { DistanceUnit } from '../settings.js';

export type LengthUnit = 'm' | 'ft';

const ALIASES: Record<LengthUnit, readonly string[]> = {
  m: ['m', 'mt', 'mts', 'metro', 'metros', 'meter', 'meters', 'metre', 'metres'],
  ft: ['ft', "'", 'pe', 'pes', 'foot', 'feet'],
};

/** A unidade da cena, se for metro ou pé, do jeito que o mestre tiver escrito ("m", "Pés", "ft."…). */
export function lengthUnitOf(text: string): LengthUnit | null {
  const key = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  return (Object.keys(ALIASES) as LengthUnit[]).find((u) => ALIASES[u].includes(key)) ?? null;
}

export type Distance = { value: number; unit: LengthUnit | null };

/**
 * A distância `value`, na unidade da cena, na unidade que a pessoa quer. Sem conversão (a
 * preferência é a da cena, ou a cena usa outra unidade), volta igual, com `unit` null: vale o
 * texto da cena.
 */
export function convertDistance(value: number, sceneUnit: string, want: Exclude<DistanceUnit, 'both'>): Distance {
  const from = lengthUnitOf(sceneUnit);
  if (want === 'scene' || !from) return { value, unit: null };
  if (from === want) return { value, unit: want };
  return { value: want === 'ft' ? (value * 10) / 3 : (value * 3) / 10, unit: want };
}

/**
 * O que mostrar para a distância `value` (na unidade da cena): uma medida, ou duas na preferência
 * "metros e pés", sempre os metros antes ("6 m (20 pés)"). Cena em outra unidade: só a dela.
 */
export function distanceParts(value: number, sceneUnit: string, want: DistanceUnit): Distance[] {
  if (want !== 'both') return [convertDistance(value, sceneUnit, want)];
  if (!lengthUnitOf(sceneUnit)) return [convertDistance(value, sceneUnit, 'scene')];
  return [convertDistance(value, sceneUnit, 'm'), convertDistance(value, sceneUnit, 'ft')];
}
